import { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { BackgroundVariant, Edge, Node } from 'reactflow';
import { Controller, useForm } from 'react-hook-form';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, Button, CircularProgress, Drawer, Grid2, Typography } from '@mui/material';
import AddCardIcon from '@mui/icons-material/AddCard';
import { LoadingButton } from '@mui/lab';
import EditIcon from '@mui/icons-material/Edit';

import { RootState } from '@/src/datastore/store';
import UseNavigation from '@/src/hooks/useNavigation';
import AllRolesService from '@/src/services/all-roles/allRolesService';
import CustomTablePaginationActions from '@/src/components/common/CustomTablePaginationActions';
import NoAuthorization from '@/src/components/common/NoAuthorization';
import AppConfig from '@/src/config/AppConfig';
import lightTheme from '@/src/styles/themes/lightTheme';
import {
  DYNAMIC_INPUT_TYPES_ENUM,
  IAttributeOptions,
  ILooseObject,
  INPUT_TYPES_ENUM,
  IPagination
} from '@/src/types/common.interface';
import DirectiveGraph from '@/src/components/common/graph-components-assets/DirectiveGraph';
import DynamicInput from '@/src/components/common/DynamicInput';
import GraphTableSwitcher from '@/src/components/common/GraphTableSwitcher';
import RoleNodeDetails from '@/src/components/RoleNodeDetails';

const Roles = () => {
  const userData = useSelector((state: RootState) => state?.userDetails);
  const appConfigData = useSelector((state: RootState) => state?.appConfigData);
  const navigate = UseNavigation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tableDetails, setTableDetails] = useState<ILooseObject>({
    columns: [],
    rows: []
  });
  const [paginationData] = useState<IPagination>({
    page: 0,
    size: -1
  });
  const [isGraphLoading, setIsGraphLoading] = useState<boolean>(false);
  const [isLoadingUpdateEdges, setIsLoadingUpdateEdges] = useState<boolean>(false);
  const [graphData, setGraphData] = useState<ILooseObject>({
    nodes: [],
    edges: []
  });
  const [availableOptions, setAvailableOptions] = useState<ILooseObject>({});
  const [isLoadingOptions, setIsLoadingOptions] = useState<ILooseObject>({});
  const [tabSelectionValue, setTabSelectionValue] = useState<number | null>(
    appConfigData?.selectedTabValue ?? 1
  );
  const [selectedNodeData, setSelectedNodeData] = useState<ILooseObject>({});
  const [isOpenRoleDrawer, setIsOpenRoleDrawer] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [tempEdges, setTempEdges] = useState<Edge[]>([]);
  const [tempNodes, setTempNodes] = useState<Node[]>([]);
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);

  const {
    control,
    formState: { dirtyFields, errors },
    watch,
    reset,
    clearErrors
  } = useForm({
    mode: 'onSubmit'
  });
  const watchApplication = watch('application');

  const uniqueApplicationKeys: string[] = Array.from(
    userData?.organizations?.reduce((acc: Set<string>, org: ILooseObject) => {
      org?.applications?.forEach((app: ILooseObject) =>
        app?.application_key ? acc.add(app?.application_key) : ''
      );
      return acc;
    }, new Set())
  );

  const userAppPermissions: ILooseObject = useSelector(
    (state: RootState) => state?.userDetails?.OptionsAndPermissions?.appPermissions
  );

  const availableApplications = useSelector(
    (state: RootState) => state?.userDetails?.OptionsAndPermissions?.applicationsOptions
  );

  const getAppPermissions = (applicationKey?: string) => {
    //filtering permissions who have users applications
    if (applicationKey) {
      return userAppPermissions?.[applicationKey];
    }
    const applicationKeys = uniqueApplicationKeys;
    return Object.entries(userAppPermissions as Record<string, string[]>)?.reduce(
      (acc: string[], [key, permissions]: [string, string[]]) => {
        if (applicationKeys?.includes(key)) {
          return Array.from(new Set([...acc, ...permissions]));
        }
        return acc;
      },
      []
    );
  };

  const getAllRoles = async (paginationData: IPagination, applicationKeys: string[]) => {
    try {
      setIsLoading(true);
      const results = await AllRolesService.getAllRolesList({
        pagination: paginationData,
        filters: {
          application_keys: applicationKeys
        }
      });

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } else if (!results.error.code) {
        setTableDetails({
          columns: results.columns,
          rows: results.rows
        });
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getAllGraphRoles = async (paginationData: IPagination, applicationKeys: string[]) => {
    try {
      setIsGraphLoading(true);
      const results = await AllRolesService.getAllGraphRolesList({
        pagination: paginationData,
        filters: {
          application_key: applicationKeys
        }
      });

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } else if (!results.error.code) {
        const { nodes, edges } = results?.data ?? { nodes: [], edges: [] };
        const formattedNodes = nodes.map((node: Node) => {
          return {
            ...node,
            position: { x: 0, y: 0 },
            type: 'custom',
            data: {
              ...node.data,
              label: node?.data?.role?.ui_label,
              isHovered: false
            }
          };
        });
        const formattedEdges = edges.map((edge: Edge) => ({
          ...edge,
          animated: false
        }));

        setGraphData({
          nodes: formattedNodes,
          edges: formattedEdges
        });
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsGraphLoading(false);
    }
  };

  const updateNodesEdges = async (
    nodes: ILooseObject[],
    edges: ILooseObject[],
    applicationKey: string
  ): Promise<ILooseObject | undefined> => {
    try {
      setIsLoadingUpdateEdges(true);
      const payload = {
        nodes: nodes,
        edges: edges
      };
      const results = await AllRolesService.updatedNodesEdgesHierarchy({
        applicationKey,
        payload
      });

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } else if (!results.error.code) {
        enqueueSnackbar('Edge changes saved successfully', {
          variant: 'success',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.success
        });
        return results?.data;
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsLoadingUpdateEdges(false);
    }
  };

  const buildAdjacencyMap = (edges: Edge[]) => {
    const adjacencyMap = new Map<string, string[]>();
    edges.forEach((edge: Edge) => {
      if (!adjacencyMap.has(edge.source)) {
        adjacencyMap.set(edge.source, []);
      }
      adjacencyMap.get(edge.source)?.push(edge.target);
    });
    return adjacencyMap;
  };

  const collectChildRoles = (
    parentId: string,
    visited = new Set<string>(),
    collected = new Map<string, ILooseObject>(),
    adjacencyMap = buildAdjacencyMap(graphData?.edges || [])
  ) => {
    if (visited.has(parentId)) return Array.from(collected.values());
    visited.add(parentId);
    // Get the child node ids from the adjacency map
    const childNodeIds = adjacencyMap.get(parentId) || [];
    // Find the child nodes by their ids
    const childNodes = graphData.nodes.filter((node: Node) => childNodeIds.includes(node?.id));

    for (const child of childNodes) {
      if (child.data?.role && !collected.has(child.id)) {
        collected.set(child.id, { ...child.data.role });
      }
      // Recursively collect from children
      collectChildRoles(child.id, visited, collected);
    }
    return Array.from(collected.values());
  };

  const handleChangeTab = (value: number) => {
    setTabSelectionValue(value);
  };

  const handleNodeClick = (_: React.MouseEvent, node: Node) => {
    const collectionNodeData: ILooseObject = node?.data;
    setIsOpenRoleDrawer(true);
    if (node && node.id) {
      // Helper function to recursively collect all child roles and permissions
      const allChildRoles = collectChildRoles(node.id);
      collectionNodeData.childRoles = allChildRoles;
    }
    setSelectedNodeData(collectionNodeData);
  };

  const handleCloseRoleDrawer = () => {
    setIsOpenRoleDrawer(false);
    setSelectedNodeData({});
  };

  const handleEditModeToggle = () => {
    if (!isEditMode) {
      // Entering edit mode - save current state as temporary
      setTempEdges([...graphData.edges]);
      setTempNodes([...graphData.nodes]);
    } else {
      // Exiting edit mode - clear hover state
      setHoveredEdgeId(null);
    }
    setIsEditMode(!isEditMode);
  };

  const handleSaveChanges = async () => {
    // Save the temporary changes to the main graph data
    const nodeIdsCollection = tempNodes?.reduce((collection: ILooseObject[], node: Node) => {
      const nodeId = { id: node.id };
      collection.push(nodeId);
      return collection;
    }, []);
    const edgeIdsCollection: ILooseObject[] = tempEdges?.reduce(
      (collection: ILooseObject[], edge: Edge) => {
        collection.push({ source: edge.source, target: edge.target });
        return collection;
      },
      []
    );
    const results = await updateNodesEdges(nodeIdsCollection, edgeIdsCollection, watchApplication);
    if (results) {
      setIsEditMode(false);
      setHoveredEdgeId(null);

      // Normalize all edges and nodes to remove temporary states
      const normalizedNodes = tempNodes.map((node: Node) => ({
        ...node,
        data: {
          ...node?.data,
          isHovered: false,
          isChild: false
        },
        style: {
          ...node?.style,
          zIndex: 1
        }
      }));

      const normalizedEdges = tempEdges.map((edge: Edge) => ({
        ...edge,
        data: {
          ...edge?.data,
          isNew: false, // Remove new marking
          hovered: false,
          deletable: false
        },
        style: {
          ...edge?.style,
          stroke: '#DADADA', // Reset to normal gray color
          strokeWidth: 1, // Reset to normal width
          cursor: 'default',
          zIndex: 1
        },
        deletable: false,
        animated: false
      }));

      setGraphData({
        nodes: normalizedNodes,
        edges: normalizedEdges
      });
    }
  };

  const handleCancelChanges = () => {
    // Revert to original state
    setTempEdges([...graphData.edges]);
    setTempNodes([...graphData.nodes]);
    setIsEditMode(false);
    setHoveredEdgeId(null);
  };

  const handleNodeConnect = (source: string | null, target: string | null) => {
    if (source && target && isEditMode) {
      // Check if edge already exists in tempEdges
      const edgeExists = tempEdges.some((edge) => edge.source === source && edge.target === target);

      if (!edgeExists) {
        // Check if this connection existed in the original graph
        const originalConnectionExists = graphData.edges.some(
          (edge: Edge) => edge.source === source && edge.target === target
        );

        const newEdge: Edge = {
          id: `edge-${source}-${target}`,
          source,
          target,
          animated: false,
          deletable: true,
          data: {
            isNew: !originalConnectionExists // Only mark as new if it didn't exist in original graph
          }
        };
        setTempEdges((prev) => [...prev, newEdge]);
      }
    }
  };

  const handleEdgeMouseEnter = (event: React.MouseEvent, edge: Edge) => {
    if (isEditMode && edge.deletable) {
      setHoveredEdgeId(edge.id);
    }
  };

  const handleEdgeMouseLeave = (event: React.MouseEvent, edge: Edge) => {
    if (isEditMode) {
      setHoveredEdgeId(null);
    }
  };

  const handleEdgeDelete = (event: React.MouseEvent, edge: Edge) => {
    if (isEditMode && edge.deletable) {
      setTempEdges((prev) => prev.filter((e) => e.id !== edge.id));
      setHoveredEdgeId(null);
    }
  };

  // Function to check if there are any changes in edges
  const hasEdgeChanges = () => {
    if (!isEditMode) return false;

    const originalEdges = graphData.edges || [];
    const currentEdges = tempEdges || [];

    // Check if number of edges changed
    if (originalEdges.length !== currentEdges.length) {
      return true;
    }

    // Check if any edge connections changed (source/target)
    // This is the most reliable way to detect changes
    const originalConnections = originalEdges
      .map((edge: Edge) => `${edge.source}-${edge.target}`)
      .sort();
    const currentConnections = currentEdges
      .map((edge: Edge) => `${edge.source}-${edge.target}`)
      .sort();

    if (JSON.stringify(originalConnections) !== JSON.stringify(currentConnections)) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    try {
      const applicationsList: IAttributeOptions[] = Object.values(availableApplications);
      setIsLoadingOptions((pre) => ({
        ...pre,
        applications: true
      }));
      if (applicationsList) {
        // const filteredApplications = applicationsList?.filter((applicationKey: IAttributeOptions) => )
        setAvailableOptions((pre: ILooseObject) => ({
          ...pre,
          application: applicationsList
        }));
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    } finally {
      setIsLoadingOptions((pre) => ({
        ...pre,
        applications: false
      }));
    }
  }, [availableApplications]);

  useEffect(() => {
    if (watchApplication) {
      if (getAppPermissions(watchApplication)?.includes('all_role_view')) {
        if (tabSelectionValue === 0) {
          getAllRoles(paginationData, watchApplication);
        } else {
          getAllGraphRoles(paginationData, watchApplication);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchApplication, tabSelectionValue]);

  if (!getAppPermissions()?.includes('all_role_view')) {
    return <NoAuthorization />;
  }

  return (
    <Box display="flex" flexDirection="column" rowGap={2}>
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" color="primary.dark">
          All Roles
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddCardIcon />}
            disabled={!getAppPermissions()?.includes('api_permission_add')} //TODO: When enable organization level add role then add the permission of oganization level also
            onClick={() => navigate.navigateTo('/roles/add-role')}
            sx={{ textTransform: 'none' }}
          >
            Add role
          </Button>
        </Box>
      </Box>
      <Box
        border={1}
        borderColor={lightTheme.palette.grey[300]}
        borderRadius={3}
        sx={{ p: 2, display: 'flex', flexDirection: 'column', rowGap: 1 }}
      >
        {isLoadingOptions?.applications ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" fontWeight={600}>
              Filter by:
            </Typography>
            <Grid2
              container
              component="form"
              display="flex"
              flexDirection="row"
              columnGap={2}
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Grid2
                display="flex"
                flexDirection="column"
                rowGap={0.5}
                key={'application'}
                size={{ xs: 4 }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Application
                </Typography>
                <Controller
                  name="application"
                  control={control}
                  render={({ field: { onChange, onBlur, value, name } }) => (
                    <DynamicInput
                      id={1}
                      label=""
                      type={DYNAMIC_INPUT_TYPES_ENUM.SELECT}
                      inputType={INPUT_TYPES_ENUM.TEXT}
                      required
                      value={value ?? ''}
                      disabled={false}
                      options={availableOptions?.application}
                      size="small"
                      helperText={(errors[name]?.message as string) ?? ' '}
                      hasError={!!(errors[name]?.message as string)}
                      onChange={onChange}
                      onBlur={onBlur}
                      disableClearable={true}
                    />
                  )}
                />
              </Grid2>
              <Button
                variant="outlined"
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  reset();
                  clearErrors();
                }}
                disabled={!(dirtyFields && Object.keys(dirtyFields)?.length > 0)}
              >
                Reset
              </Button>
            </Grid2>
            <GraphTableSwitcher
              handleChangeTab={handleChangeTab}
              selectedTabValue={tabSelectionValue}
            />
          </>
        )}
      </Box>
      {watchApplication?.length > 0 &&
        (getAppPermissions(watchApplication)?.includes('all_role_view') ? (
          tabSelectionValue === 0 ? (
            <DataGrid
              getRowId={(row) => row?.role_key}
              rows={tableDetails.rows}
              columns={tableDetails.columns}
              disableRowSelectionOnClick
              disableColumnSelector
              disableColumnMenu
              disableDensitySelector
              slots={{ toolbar: GridToolbar }}
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-columnHeaderTitle': {
                  textOverflow: 'clip',
                  whiteSpace: 'break-spaces',
                  lineHeight: 1.15,
                  fontWeight: 600
                },
                '& .MuiDataGrid-cell': {
                  whiteSpace: 'normal',
                  lineHeight: 'normal',
                  padding: '8px 16px'
                },
                '& .MuiDataGrid-row': {
                  minHeight: '52px !important'
                },
                '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus': {
                  outline: 'none'
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none'
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none'
                },
                '& .MuiDataGrid-main': {
                  overflow: 'unset'
                },
                '& .MuiDataGrid-columnHeaders': {
                  position: 'sticky',
                  top: 63,
                  zIndex: 1
                },
                '& .MuiDataGrid-container--top': {
                  zIndex: 1
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: lightTheme.palette.grey[100]
                }
              }}
              slotProps={{
                pagination: {
                  ActionsComponent: CustomTablePaginationActions
                },
                toolbar: {
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 }
                }
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 100, page: 0 }
                }
              }}
              paginationMode="client"
              loading={isLoading}
              pageSizeOptions={[20, 50, 100]}
            />
          ) : (
            <Box>
              {isGraphLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {tabSelectionValue === 1 && (
                    <Box display="flex" justifyContent="flex-end" columnGap={1}>
                      {!isEditMode ? (
                        <LoadingButton
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={handleEditModeToggle}
                          sx={{ textTransform: 'none' }}
                        >
                          Edit roles
                        </LoadingButton>
                      ) : (
                        <>
                          <LoadingButton
                            variant="contained"
                            size="small"
                            onClick={handleSaveChanges}
                            disabled={!hasEdgeChanges()}
                            loading={isLoadingUpdateEdges}
                            sx={{ textTransform: 'none' }}
                          >
                            Save
                          </LoadingButton>
                          <LoadingButton
                            variant="outlined"
                            size="small"
                            onClick={handleCancelChanges}
                            disabled={isLoadingUpdateEdges}
                            sx={{ textTransform: 'none' }}
                          >
                            Cancel
                          </LoadingButton>
                        </>
                      )}
                    </Box>
                  )}
                  <DirectiveGraph
                    initialEdges={isEditMode ? tempEdges : graphData?.edges}
                    initialNodes={isEditMode ? tempNodes : graphData?.nodes}
                    backgroundVariant={BackgroundVariant.Cross}
                    onNodeClick={handleNodeClick}
                    isNodeConnectable={isEditMode}
                    isDeletableNodesAndEdges={isEditMode}
                    onNodeConnect={handleNodeConnect}
                    onEdgeClick={handleEdgeDelete}
                    onEdgeMouseEnter={handleEdgeMouseEnter}
                    onEdgeMouseLeave={handleEdgeMouseLeave}
                    hoveredEdgeId={hoveredEdgeId}
                  />
                  <Drawer
                    anchor="right"
                    open={isOpenRoleDrawer}
                    onClose={handleCloseRoleDrawer}
                    PaperProps={{
                      sx: { width: 500, maxWidth: '70vw' } // Set your desired width here
                    }}
                  >
                    <RoleNodeDetails roleDetails={selectedNodeData} />
                  </Drawer>
                </>
              )}
            </Box>
          )
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <NoAuthorization />
          </Box>
        ))}
    </Box>
  );
};

export default Roles;
